import React, { useState, useEffect, useRef } from 'react';
import StoreButtons from '../../../components/common/StoreButtons';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import FacebookLogin from '@greatsumini/react-facebook-login';
import { FacebookLogin as NativeFacebookLogin } from '@capacitor-community/facebook-login';
import { useAuth } from '../../../contexts/AuthContext';
import { nativeAppleSignIn } from '../../../services/appleAuthService';
import { nativeGoogleSignIn } from '../../../services/googleAuthService';
import { Capacitor } from '@capacitor/core';
const isCapacitor = Capacitor.isNativePlatform();
const isIOS = isCapacitor && Capacitor.getPlatform() === 'ios';
import { useTheme } from '../../../contexts/ThemeContext';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import { User, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import Logo from '../../../components/common/Logo';
import { apiCall } from '../../../services/apiUtil';
import { getConfig } from '../../../config';

export default function SignUpPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signup, googleLogin, facebookLogin, appleLogin, isLoading } = useAuth();
  const { theme } = useTheme();
  const { setBreadcrumbItems } = useBreadcrumb();
  const [buttonWidth, setButtonWidth] = useState(400);
  const buttonContainerRef = useRef(null);

  useEffect(() => {
    if (buttonContainerRef.current) {
      setButtonWidth(buttonContainerRef.current.offsetWidth);
    }
  }, []);

  // Get invitation params from URL
  const invitationId = searchParams.get('invitation');
  const groupId = searchParams.get('groupId');
  const groupName = searchParams.get('groupName');
  const landingUrl = searchParams.get('landingUrl');

  // Claim mode: detect ?userId= param
  const claimUserId = searchParams.get('userId');
  const isClaimMode = !!claimUserId;
  const [claimedPlayerName, setClaimedPlayerName] = useState('');
  const [claimInvalid, setClaimInvalid] = useState(false);
  const [claimLoading, setClaimLoading] = useState(false);

  // Clear any stale tokens when landing on sign-up page
  useEffect(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('socialUser');

    // Store landing URL if provided in invitation
    if (landingUrl) {
      localStorage.setItem('postLoginRedirect', landingUrl);
    }
  }, [landingUrl]);

  // In claim mode: fetch the player's name to display
  useEffect(() => {
    if (!isClaimMode) return;
    (async () => {
      try {
        const info = await apiCall(`/auth/claim-info/${claimUserId}`);
        if (!info?.valid) {
          setClaimInvalid(true);
          return;
        }
        setClaimedPlayerName(`${info.firstName || ''} ${info.lastName || ''}`.trim());
      } catch {
        setClaimInvalid(true);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClaimMode, claimUserId]);

  // Set breadcrumb items
  useEffect(() => {
    setBreadcrumbItems([
      { label: 'Home', path: '/' },
      { label: 'Sign Up' }
    ]);

    // Clear breadcrumbs on unmount
    return () => setBreadcrumbItems([]);
  }, [setBreadcrumbItems]);

  // Helper function to get proper input text color
  const getInputTextColor = () => {
    if (theme.input.includes('text-white')) return '#ffffff';
    if (theme.input.includes('text-gray-900') || theme.input.includes('text-green-900') || theme.input.includes('text-cyan-900') || theme.input.includes('text-orange-900') || theme.input.includes('text-purple-900')) return '#111827';
    return '#111827'; // default to dark text
  };

  const handleGoogleSignUp = async (tokenResponse) => {
    const result = await googleLogin(tokenResponse.credential, 'signup');
    if (result.success) {
      const storedRedirect = localStorage.getItem('postLoginRedirect');
      if (storedRedirect) localStorage.removeItem('postLoginRedirect');
      if (result.linked) {
        sessionStorage.setItem('linkedAccountMessage', 'Your existing account has been linked with Google. You can now sign in with either Google or your password.');
      }
      window.location.href = storedRedirect || '/';
    } else {
      setError(result.error || 'Google sign-up failed. Please try again.');
    }
  };

  const handleFacebookSignUp = async (response) => {
    const result = await facebookLogin(response.accessToken, response.authenticationToken, 'signup');
    if (result.success) {
      const storedRedirect = localStorage.getItem('postLoginRedirect');
      if (storedRedirect) localStorage.removeItem('postLoginRedirect');
      if (result.linked) {
        sessionStorage.setItem('linkedAccountMessage', 'Your existing account has been linked with Facebook. You can now sign in with either Facebook or your password.');
      }
      window.location.href = storedRedirect || '/';
    } else {
      setError(result.error || 'Facebook sign-up failed. Please try again.');
    }
  };

  const handleAppleSignUp = async () => {
    try {
      const { identityToken, firstName, lastName } = await nativeAppleSignIn();
      const result = await appleLogin(identityToken, firstName, lastName);
      if (result.success) {
        const storedRedirect = localStorage.getItem('postLoginRedirect');
        if (storedRedirect) localStorage.removeItem('postLoginRedirect');
        window.location.href = storedRedirect || '/';
      } else {
        setError(result.error || 'Apple Sign-In failed. Please try again.');
      }
    } catch (e) {
      setError('Apple Sign-In was cancelled or failed.');
    }
  };

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const validatePassword = (password) => {
    const requirements = {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
    };

    const missingRequirements = [];
    if (!requirements.minLength) missingRequirements.push('at least 8 characters');
    if (!requirements.hasUppercase) missingRequirements.push('one uppercase letter');
    if (!requirements.hasLowercase) missingRequirements.push('one lowercase letter');
    if (!requirements.hasNumber) missingRequirements.push('one number');
    if (!requirements.hasSpecialChar) missingRequirements.push('one special character');

    return {
      isValid: Object.values(requirements).every(req => req),
      missingRequirements
    };
  };

  const validateForm = () => {
    if (!termsAccepted) {
      setError('You must accept the Terms of Service and Privacy Policy to continue.');
      return false;
    }

    if (isClaimMode) {
      if (!formData.email || !formData.password || !formData.confirmPassword) {
        setError('Please fill in all required fields');
        return false;
      }
    } else {
      if (!formData.firstName || !formData.lastName || !formData.email || !formData.password || !formData.confirmPassword) {
        setError('Please fill in all required fields');
        return false;
      }
    }

    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      setError(`Password must include: ${passwordValidation.missingRequirements.join(', ')}`);
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    if (isClaimMode) {
      // Claim account flow
      setClaimLoading(true);
      try {
        const result = await apiCall('/auth/claim-account', {
          method: 'POST',
          body: JSON.stringify({
            userId: claimUserId,
            email: formData.email,
            password: formData.password
          })
        });
        // Store token and auto-sign in — email is already verified via invitation link
        if (result.token) {
          localStorage.setItem('token', result.token);
          localStorage.setItem('socialUser', JSON.stringify({
            id: result.id,
            email: result.email,
            firstName: result.firstName,
            lastName: result.lastName
          }));
        }
        const redirectTo = localStorage.getItem('postLoginRedirect') || '/';
        localStorage.removeItem('postLoginRedirect');
        window.location.href = redirectTo;
      } catch (err) {
        if (err.message && err.message.toLowerCase().includes('match')) {
          setError('The email you entered does not match. Please use the email your manager registered for you.');
        } else {
          setError(err.message || 'Failed to activate account. Please try again.');
        }
      } finally {
        setClaimLoading(false);
      }
      return;
    }

    try {
      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        phoneNumber: ''
      };


      const result = await signup(userData);
      if (result.success) {
        navigate('/signin', {
          state: {
            signupSuccess: true,
            message: 'Account created! Please check your email and click the verification link before signing in.',
            redirectAfterLogin: landingUrl || null
          }
        });
      } else {
        setError(result.error || 'Signup failed. Please try again.');
      }

    } catch (error) {
      console.error('Signup error:', error);
      setError(error.message || 'An error occurred during signup. Please try again.');
    }
  };

  // Claim mode: invalid link
  if (isClaimMode && claimInvalid) {
    return (
      <div className={`min-h-screen py-12 px-4 sm:px-6 lg:px-8 ${theme.background}`}>
        <div className="max-w-md mx-auto">
          <div className={`rounded-2xl shadow-xl p-8 ${theme.card} text-center`}>
            <Logo size="large" />
            <h2 className={`text-2xl font-bold mt-6 mb-3 ${theme.text}`}>Invalid Link</h2>
            <p className={`${theme.textSecondary}`}>
              This account activation link is invalid or the account has already been claimed.
            </p>
            <Link to="/signin" className="inline-block mt-6 text-blue-500 hover:text-blue-600 font-medium">
              Go to Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen py-12 px-4 sm:px-6 lg:px-8 ${theme.background}`}>
      <div className="max-w-md mx-auto">
        <div className={`rounded-2xl shadow-xl p-8 ${theme.card}`}>
          <div className="text-center mb-8">
            <Logo size="large" />
            {isClaimMode ? (
              <>
                <h2 className={`text-3xl font-bold mt-6 ${theme.text}`}>Claim Your Account</h2>
                {claimedPlayerName && (
                  <p className={`mt-2 ${theme.textSecondary}`}>
                    Welcome, <strong>{claimedPlayerName}</strong>! Set your password to activate your account.
                  </p>
                )}
                {!claimedPlayerName && (
                  <p className={`mt-2 ${theme.textSecondary}`}>
                    Set your password to activate your account
                  </p>
                )}
              </>
            ) : (
              <>
                <h2 className={`text-3xl font-bold mt-6 ${theme.text}`}>Join Kynaix Sports</h2>
                <p className={`mt-2 ${theme.textSecondary}`}>
                  Create your account to get started
                </p>
              </>
            )}
          </div>

          {/* Terms acceptance — shown for all signup flows including claim */}
          <label className="flex items-start gap-3 mb-5 cursor-pointer">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => { setTermsAccepted(e.target.checked); if (error) setError(''); }}
              className="mt-0.5 w-4 h-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500 cursor-pointer flex-shrink-0"
            />
            <span className={`text-sm ${theme.textSecondary}`}>
              I agree to the{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-600 underline" onClick={(e) => e.stopPropagation()}>
                Terms of Service
              </a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-600 underline" onClick={(e) => e.stopPropagation()}>
                Privacy Policy
              </a>
            </span>
          </label>

          {!isClaimMode && (
            <>
              {/* Google sign up — blocked until terms accepted */}
              <div
                ref={buttonContainerRef}
                className={`w-full mb-4 ${!termsAccepted ? 'opacity-50 pointer-events-none' : ''}`}
                title={!termsAccepted ? 'Please accept the Terms of Service and Privacy Policy first' : ''}
              >
                {isCapacitor ? (
                  <button
                    type="button"
                    disabled={!termsAccepted}
                    onClick={async () => {
                      try {
                        const idToken = await nativeGoogleSignIn();
                        await handleGoogleSignUp({ credential: idToken });
                      } catch (e) {
                        console.error(`[GoogleSignUp] caught: ${e?.message ?? JSON.stringify(e)} code=${e?.code}`);
                        setError('Google sign-up was cancelled or failed.');
                      }
                    }}
                    className="flex items-center justify-center gap-3 w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-white text-gray-700 font-medium text-sm hover:bg-gray-50 transition-colors"
                  >
                    <svg width="18" height="18" viewBox="0 0 48 48">
                      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                      <path fill="none" d="M0 0h48v48H0z"/>
                    </svg>
                    Sign up with Google
                  </button>
                ) : (
                  <GoogleLogin
                    onSuccess={handleGoogleSignUp}
                    onError={() => setError('Google sign-up was cancelled or failed.')}
                    text="signup_with"
                    width={buttonWidth}
                    shape="rectangular"
                    logo_alignment="left"
                    useOneTap={false}
                  />
                )}
              </div>

              {/* Sign in with Apple — native iOS only */}
              {isIOS && (
                <button
                  type="button"
                  onClick={handleAppleSignUp}
                  disabled={!termsAccepted}
                  title={!termsAccepted ? 'Please accept the Terms of Service and Privacy Policy first' : ''}
                  className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg bg-black hover:bg-gray-900 text-white font-medium transition-colors mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg width="18" height="18" viewBox="0 0 814 1000" fill="white">
                    <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 790.7 0 663 0 541.8c0-207.5 135.4-317.3 269-317.3 70.1 0 128.4 46.4 172.5 46.4 42.8 0 109.9-49 192.5-49 30.8 0 108.2 2.6 168.4 71.9z"/>
                    <path d="M554.1 107.3c23.5-28.2 39.5-67.4 39.5-106.6 0-5.1-.3-10.3-1.2-14.4-37.5 1.3-81.5 24.6-108.8 55.1-21.1 23.5-40.8 62.2-40.8 101.9 0 5.8.9 11.5 1.3 13.4 2.3.3 5.8.6 9.2.6 33.8 0 75.8-22.3 100.8-50z"/>
                  </svg>
                  Sign up with Apple
                </button>
              )}

              {/* Facebook sign-up hidden until app goes Live on Facebook Developer Console */}
              {false && isCapacitor ? (
                <button
                  type="button"
                  disabled={!termsAccepted}
                  title={!termsAccepted ? 'Please accept the Terms of Service and Privacy Policy first' : ''}
                  onClick={async () => {
                    try {
                      const result = await NativeFacebookLogin.login({
                        permissions: ['public_profile', 'email'],
                        tracking: 'enabled',
                      });
                      const token = result?.accessToken?.token;
                      if (token) {
                        handleFacebookSignUp({ accessToken: token });
                      } else {
                        setError('Facebook sign-up was cancelled or failed.');
                      }
                    } catch (e) {
                      setError('Facebook sign-up was cancelled or failed.');
                    }
                  }}
                  className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg bg-[#1877F2] hover:bg-[#166FE5] text-white font-medium transition-colors mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Sign up with Facebook
                </button>
              ) : null}

              <div className="flex items-center gap-3 mb-4">
                <div className={`flex-1 h-px border-t ${theme.border}`} />
                <span className={`text-sm ${theme.textSecondary}`}>or sign up with email</span>
                <div className={`flex-1 h-px border-t ${theme.border}`} />
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {!isClaimMode && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${theme.text}`}>First Name *</label>
                  <div className="relative">
                    <User className={`w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 ${theme.textMuted}`} />
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      placeholder="First name"
                      required
                      autoComplete="given-name"
                      className={`w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${theme.input} placeholder:text-gray-400`}
                      style={{ color: getInputTextColor() }}
                    />
                  </div>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${theme.text}`}>Last Name *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Last name"
                    required
                    autoComplete="family-name"
                    className={`w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${theme.input} placeholder:text-gray-400`}
                    style={{ color: getInputTextColor() }}
                  />
                </div>
              </div>
            )}

            <div>
              <label className={`block text-sm font-medium mb-2 ${theme.text}`}>Email Address *</label>
              {isClaimMode && (
                <p className={`text-xs mb-1 ${theme.textMuted}`}>Enter the email registered for this account</p>
              )}
              <div className="relative">
                <Mail className={`w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 ${theme.textMuted}`} />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your@email.com"
                  required
                  autoComplete="email"
                  className={`w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${theme.input} placeholder:text-gray-400`}
                  style={{ color: getInputTextColor() }}
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${theme.text}`}>Password *</label>
              <div className="relative">
                <Lock className={`w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 ${theme.textMuted}`} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Create a password"
                  required
                  autoComplete="new-password"
                  className={`w-full pl-10 pr-10 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${theme.input} placeholder:text-gray-400`}
                  style={{ color: getInputTextColor() }}
                />
                <button type="button" onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className={`text-xs mt-1 ${theme.textMuted}`}>
                Must include uppercase, lowercase, number, and special character
              </p>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${theme.text}`}>Confirm Password *</label>
              <div className="relative">
                <Lock className={`w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 ${theme.textMuted}`} />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirm your password"
                  required
                  autoComplete="new-password"
                  className={`w-full pl-10 pr-10 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${theme.input} placeholder:text-gray-400`}
                  style={{ color: getInputTextColor() }}
                />
                <button type="button" onClick={() => setShowConfirmPassword(v => !v)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700">
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isClaimMode ? (claimLoading || !termsAccepted) : (isLoading || !termsAccepted)}
              title={!termsAccepted ? 'Please accept the Terms of Service and Privacy Policy first' : ''}
              className={`w-full py-3 rounded-lg font-semibold hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed ${theme.gradient} text-white`}
            >
              {isClaimMode
                ? (claimLoading ? 'Activating...' : 'Activate Account')
                : (isLoading ? 'Creating Account...' : 'Create Account')
              }
            </button>
          </form>

          {!isClaimMode && (
            <div className="mt-6 flex flex-col items-center gap-4">
              <div className="flex items-center gap-3">
                <span className={`text-sm ${theme.textSecondary}`}>Already have an account?</span>
                <Link to="/signin" className={`text-sm font-semibold px-4 py-1.5 rounded-lg border ${theme.border} ${theme.text} ${theme.cardHover} transition-colors`}>Sign in</Link>
              </div>
              {!isCapacitor && (
                <div className="flex flex-col items-center gap-2">
                  <span className={`text-xs ${theme.textMuted}`}>Or get the app</span>
                  <StoreButtons size="sm" />
                </div>
              )}
            </div>
          )}
          {isClaimMode && (
            <p className={`text-center mt-6 text-sm ${theme.textMuted}`}>
              This account was created for you. Setting a password activates it and you'll be responsible for managing it.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}